import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ExtendedWorkspaceListItem } from '@futrli/views/operations/workspaces/processors/workspacesList';
import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

export const useWorkspaceSettingsForm = (workspace?: ExtendedWorkspaceListItem) => {
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<WizardFormErrors>({});
  const [isDefaultWorkspace, setIsDefaultWorkspace] = useState(false);

  const intl = useIntl();
  const hasBeenValidated = useRef(false);

  // Initialize form values from workspace
  useEffect(() => {
    if (workspace) {
      setName(workspace.name || '');
      setIsDefaultWorkspace(!!workspace.member?.is_default_workspace);
      hasBeenValidated.current = false;
      setErrors({});
    }
  }, [workspace]);

  const validate = useCallback(() => {
    const newErrors: WizardFormErrors = {};

    if (workspace?.actions.updateName && !name.trim().length) {
      newErrors.name = {
        id: 'workspace-name',
        message: intl.formatMessage({
          id: 'workspaces.validation.workspace_name_is_required',
          defaultMessage: 'Workspace name is required'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.values(newErrors).length === 0;
  }, [intl, name, workspace?.actions.updateName]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  const onChangeName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const onChangeDefaultWorkspace = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setIsDefaultWorkspace(event.target.checked);
  }, []);

  const getRequestData = useCallback(() => {
    const data: { name?: string; is_default_workspace?: boolean } = {};
    if (workspace?.actions.updateName) {
      data.name = name;
    }
    if (workspace?.actions.setAsDefault) {
      data.is_default_workspace = isDefaultWorkspace;
    }
    return data;
  }, [workspace?.actions.updateName, workspace?.actions.setAsDefault, name, isDefaultWorkspace]);

  return {
    errors,
    name,
    isDefaultWorkspace,
    getRequestData,
    onChangeName,
    onChangeDefaultWorkspace,
    revalidate,
    validate
  };
};
